import { LandingSiteFormDtoIn, LandingSiteFormVersionDtoIn } from '@/models/in/LandingSiteFormDtoIn';

export const LANDING_SITE_FORMS_URL = 'management/landing-site-forms';

/** Kept in step with the same limit in LandingSiteFormsService, so a too-large file is caught here. */
export const MAX_DOCUMENT_SIZE_MB = 15;

/** Kept in step with MaxDetailValueLength in LandingSiteFormsService. */
export const MAX_DETAIL_LENGTH = 2000;

/**
 * The page on the landing site that shows one details entry and nothing else. A details entry has
 * no address of its own on the API - it is a popup inside the home page - but the office does need
 * one to send a donor who is only being asked for a bank transfer, and these are it.
 *
 * The paths mirror the routes in LandingSite/LandingSite.client/src/App.tsx. Keep the two in step.
 */
export const PUBLIC_DETAILS_PAGES: Record<string, string> = {
  'bank-details-israel': 'https://www.hatzdaka.org/bank',
  'bank-details-abroad': 'https://www.hatzdaka.org/bank-abroad',
};

type Entry = LandingSiteFormDtoIn | LandingSiteFormVersionDtoIn;

/**
 * What the entry is, in the room a tile has: a document's name and size, the site a link leads to,
 * or the first line of a set of particulars - a full address, or all fourteen values, would not fit
 * and would not be read anyway.
 */
export const summarizeForTile = (entry: Entry): string => {
  if (entry.details) return openingLineOf(entry.details);
  return entry.fileName ? describeDocument(entry) : hostOf(entry.url);
};

/**
 * The same, spelled out. Used wherever there is room for it and, above all, in the history: two
 * versions of one link entry differ only further along the address, and two versions of one set of
 * particulars in a single figure, so shortening either there would make them impossible to tell
 * apart.
 */
export const describeInFull = (entry: Entry): string => {
  if (entry.details) return Object.values(entry.details).map(onOneLine).join(' · ');
  return entry.fileName ? describeDocument(entry) : entry.url ?? '';
};

const describeDocument = (entry: Entry): string =>
  entry.fileSizeBytes ? `${entry.fileName} · ${formatSize(entry.fileSizeBytes)}` : `${entry.fileName}`;

const formatSize = (bytes: number): string => {
  const megabytes = bytes / (1024 * 1024);
  return megabytes >= 1 ? `${megabytes.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

/** Falls back to the address itself if it will not parse, and to nothing if there is none. */
const hostOf = (url: string | null): string => {
  if (!url) return '';

  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

/** The room a tile has for the state of an entry, before it starts stretching the row. */
const TILE_SUMMARY_LENGTH = 40;

/**
 * The opening of a set of particulars - the account name on one bank entry, the first words of the
 * bank's letter on the other - which is what tells one tile from another at a glance. Trimmed to a
 * line: the entry is read in full by opening it, not off the tile.
 */
const openingLineOf = (details: Record<string, string>): string => {
  const opening = firstLineOf(Object.values(details)[0] ?? '');

  return opening.length > TILE_SUMMARY_LENGTH ? `${opening.slice(0, TILE_SUMMARY_LENGTH).trimEnd()}…` : opening;
};

const firstLineOf = (value: string): string => value.split('\n')[0].trim();

/** A paragraph belongs to the popup, not to a one-line summary of a version. */
const onOneLine = (value: string): string => value.replace(/\s*\n\s*/g, ' ').trim();
